import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ConfirmationService } from 'primeng/api';
import { map, tap } from 'rxjs/operators';
import { ICreateGrupo, IGrupo, IUpdateGrupo } from '../../interfaces/grupos.interface';
import { selectAsistenciasList, selectGruposList } from '../../store/selectors/grupos.selectors';
import { IAppState } from '../../store/app.state';
import { DatePipe } from '@angular/common';
import { selectCentrosList } from 'src/app/store/selectors/centros.selectors';
import { GetCentros } from 'src/app/store/actions/centros.actions';
import { selectPersonasList } from 'src/app/store/selectors/personas.selectors';
import { GetPersonas } from 'src/app/store/actions/personas.actions';
import { combineLatest } from 'rxjs';
import { DownloadService } from 'src/app/services/download.service';
import { Table } from 'primeng/table';
import {
  AddIntegranteGrupo,
  CreateAsistencias,
  CreateAsistenciasSuccess,
  CreateGrupo, CreateGrupoSuccess, DeleteGrupo, DeleteGrupoSuccess, DeleteIntegranteGrupo,
  GetAsistencias,
  GetGrupos, GetSesiones, GetSesionesSuccess, UpdateGrupo, UpdateGrupoSuccess
} from '../../store/actions/grupos.actions';
import { selectBeneficiariosList } from 'src/app/store/selectors/beneficiarios.selectors';
import { GetBeneficiarios } from 'src/app/store/actions/beneficiarios.actions';
import { selectProgramasList } from 'src/app/store/selectors/programas.selectors';
import { GetProgramas } from 'src/app/store/actions/programas.actions';
import { selectFormulariosList } from 'src/app/store/selectors/formularios.selectors';
import { CreateFormularioGrupo, CreateFormularioGrupoSuccess, GetFormularios } from 'src/app/store/actions/formularios.actions';
import { ExportExcelService } from 'src/app/services/export-excel.service';

@Component({
  selector: 'app-grupos',
  templateUrl: './grupos.component.html',
  styleUrls: ['./grupos.component.scss']
})
export class GruposComponent implements OnInit, OnDestroy {
  @ViewChild('dt', { static: false }) table: Table;

  beneficiarios = [];
  asistenciasGrupo = [];
  beneficiariosNoInscritos = [];
  beneficiariosInscritos = [];

  programas = this.store$.pipe(
    select(selectProgramasList),
    map(programas => programas.map(programa => {
      return { label: programa.programa_social, value: programa.programa_social }
    }))
  );

  combined = combineLatest([
    this.store$.select(selectGruposList),
    this.store$.select(selectCentrosList),
    this.store$.select(selectPersonasList)
  ]);

  grupos = this.combined.pipe(
    map(([grupos, centros, personas]) => {
      return grupos.map(grupo => {
        this.gruposExcel.push(grupo);
        const centroFound = centros.filter(centro => centro._id === grupo.centro)[0] || { nombre: '', comunidad: '' };
        const personaFound = personas.filter(persona => persona._id === grupo.trabajador_asignado)[0] || { nombre: '', apellido_paterno: '', apellido_materno: '' };
        return {
          ...grupo,
          tipo_servicio: grupo.tipo_servicio === '' ? 'N/A' : grupo.tipo_servicio,
          programa: grupo.programa === '' ? 'N/A' : grupo.programa,
          modalidad: grupo.modalidad === '' ? 'N/A' : grupo.modalidad,
          persona_asignada: `${personaFound.nombre} ${personaFound.apellido_paterno} ${personaFound.apellido_materno}` === '' ? 'N/A' : `${personaFound.nombre} ${personaFound.apellido_paterno} ${personaFound.apellido_materno}`,
          centro_asignado: `${centroFound.nombre}` === '' ? 'N/A' : `${centroFound.nombre}`,
          tamaño_integrantes: grupo.integrantes.length,
          f_inicio: this.datePipe.transform(grupo.fecha_inicio, 'dd/MM/yyyy'),
          f_fin: this.datePipe.transform(grupo.fecha_fin, 'dd/MM/yyyy'),
          fecha_inicio: new Date(grupo.fecha_inicio),
          fecha_fin: new Date(grupo.fecha_fin),
        }
      })
    })
  );

  centros = this.store$.pipe(
    select(selectCentrosList),
    map(centros => centros.map(centro => {
      return { label: centro.nombre, value: centro._id }
    }))
  );

  personas = this.store$.pipe(
    select(selectPersonasList),
    map(personas => personas.map(persona => {
      return { label: `${persona.nombre} ${persona.apellido_paterno} ${persona.apellido_materno} - ${persona.tipo}`, value: persona._id }
    }))
  );

  beneficiariosSubscription = this.store$.pipe(
    select(selectBeneficiariosList),
    tap(data => this.beneficiarios = data)
  ).subscribe();

  asistenciasSubscription = this.store$.pipe(
    select(selectAsistenciasList),
  ).subscribe(data => {
    if (this.selectedGrupo === undefined) return

    this.header = this.selectedGrupo.nombre;
    this.displayAsistencia = true;

    const rows = data.map(usuario => {
      const user = { ...usuario }
      for (const sesiones of usuario.sesiones) {
        user[sesiones.sesionId] = sesiones.asistencia
      }

      return user;
    }).sort((a, b) => {
      if (a._id > b._id) {
        return 1;
      } else if (a._id < b._id) {
        return -1;
      } else {
        return 0;
      }
    });

    const colsAsistencias = this.sesiones.map(sesion => {
      return { header: sesion.nombre, field: sesion._id }
    });

    this.colsAsistencia = [...colsAsistencias];
    this.beneficiariosInscritos = [...rows];
  });

  formularios = this.store$.pipe(
    select(selectFormulariosList),
    map(formularios => formularios.map(formulario => {
      return { label: formulario.titulo, value: formulario.form }
    }))
  );

  createFormulariosSubscription = this.actions$.pipe(
    ofType(CreateFormularioGrupoSuccess),
  ).subscribe(_ => {
    this.spinner = false;
  });

  getSesionesSubscription = this.actions$.pipe(
    ofType(GetSesionesSuccess),
  ).subscribe(data => {
    this.sesiones = data.sesiones;
    this.store$.dispatch(GetAsistencias({ id: this.selectedGrupo._id }));
  });

  createAsistenciasSubscription = this.actions$.pipe(
    ofType(CreateAsistenciasSuccess),
  ).subscribe(_ => {
    this.store$.dispatch(GetSesiones({ id: this.selectedGrupo._id }));
  });

  cols: any[];
  colsAsistencia: any[];

  mode = '';
  header = '';
  displayForm = false;
  displayPicklist = false;
  displayAsistencia = false;
  displayFormulario = false;
  formularioAsignado: any;
  formularioAsignadoData: any;
  spinner = false;

  grupoDuplicate: any;
  selectedGrupo: any;
  sesiones: any;
  sesionId: any;
  formGrupo: FormGroup;

  gruposExcel = [];
  dataForExcel = [];

  downloadItems = [
    { label: 'CSV', command: () => { this.downloadService.exportCSV('grupos', this.table) } },
    { label: 'PDF', command: () => { this.downloadService.exportPdf('grupos', this.table) } },
  ];

  modalidad = [
    { label: 'Presencial', value: 'Presencial' },
    { label: 'Virtual', value: 'Virtual' },
    { label: 'Mixta', value: 'Mixta' }
  ];

  dias = [
    { label: 'Lunes', value: 'Lunes' },
    { label: 'Martes', value: 'Martes' },
    { label: 'Miercoles', value: 'Miercoles' },
    { label: 'Jueves', value: 'Jueves' },
    { label: 'Viernes', value: 'Viernes' },
    { label: 'Sabado', value: 'Sabado' },
  ];

  serviciosDrop = [
    { label: 'Alfabetización digital', value: 'Alfabetización digital' },
    { label: 'Proyecto de vida', value: 'Proyecto de vida' },
    { label: 'Educación financiera', value: 'Educación financiera' },
    { label: 'Educación en emprendimiento', value: 'Educación en emprendimiento' },
    { label: 'Talleres de oficio', value: 'Talleres de oficio' },
    { label: 'Mentoría', value: 'Mentoría' },
    { label: 'Asesoría en negocios', value: 'Asesoría en negocios' },
    { label: 'Capital semilla', value: 'Capital semilla' },
    { label: 'Vinculación al mercado', value: 'Vinculación al mercado' },
    { label: 'Vinculación a trabajo formal', value: 'Vinculación a trabajo formal' },
  ];

  es = {
    firstDayOfWeek: 1,
    dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
    monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    today: 'Hoy',
    clear: 'Borrar'
  };

  formSubmitSubscription = this.actions$.pipe(
    ofType(UpdateGrupoSuccess, DeleteGrupoSuccess, CreateGrupoSuccess),
    tap(_ => {
      this.store$.dispatch(GetGrupos());
      this.displayForm = false;
    })
  ).subscribe();

  constructor(
    private formBuilder: FormBuilder,
    private confirmationService: ConfirmationService,
    private store$: Store<IAppState>,
    private actions$: Actions,
    private datePipe: DatePipe,
    public downloadService: DownloadService,
    private ete: ExportExcelService
  ) {
    this.formGrupo = this.formBuilder.group({
      _id: new FormControl(''),
      nombre: new FormControl(''),
      fecha_inicio: new FormControl(''),
      fecha_fin: new FormControl(''),
      centro: new FormControl(''),
      tamaño_integrantes: new FormControl(''),
      trabajador_asignado: new FormControl(''),
      programa: new FormControl(''),
      num_sesiones: new FormControl(''),
      horas_sesion: new FormControl(''),
      modalidad: new FormControl(''),
      dias: new FormControl(''),
      horario: new FormControl(''),
      tipo_servicio: new FormControl('')
    });

    this.store$.dispatch(GetCentros());
    this.store$.dispatch(GetPersonas());
    this.store$.dispatch(GetBeneficiarios());
    this.store$.dispatch(GetGrupos());
    this.store$.dispatch(GetProgramas());
    this.store$.dispatch(GetFormularios());
  }

  async ngOnInit() {
    this.cols = [
      { field: 'nombre', header: 'Nombre', exportable: true },
      { field: 'centro_asignado', header: 'Centro Comunitario', exportable: true },
      { field: 'tipo_servicio', header: 'Tipo de Servicio Ofrecido', exportable: true },
      { field: 'f_inicio', header: 'Fecha Inicio', exportable: true },
      { field: 'f_fin', header: 'Fecha Fin', exportable: true },
      { field: 'tamaño_integrantes', header: '# de ocupantes', exportable: true },
      { field: 'acciones', header: 'Acciones', exportable: false }
    ];
  }

  async ngOnDestroy() {
    this.formSubmitSubscription.unsubscribe();
    this.beneficiariosSubscription.unsubscribe();
    this.asistenciasSubscription.unsubscribe();
  }

  verIntegrantes(grupo: IGrupo): void {
    this.formGrupo.patchValue({
      ...grupo
    });
    this.beneficiariosNoInscritos = this.beneficiarios.filter(beneficiario => !grupo.integrantes.includes(beneficiario._id))
    this.beneficiariosInscritos = this.beneficiarios.filter(beneficiario => grupo.integrantes.includes(beneficiario._id))
    this.displayPicklist = true;
  }

  hidePickList() {
    this.store$.dispatch(GetGrupos())
  }

  addIntegrante(event) {
    const colsAsistencias = [];
    const form: IGrupo = this.formGrupo.getRawValue();

    const payload = {
      beneficiarios: event.items.map(item => item._id)
    }

    this.store$.dispatch(AddIntegranteGrupo({ id: form._id, integrante: payload }))
  }

  removeIntegrante(event) {
    const form: IGrupo = this.formGrupo.getRawValue();
    const payload = {
      beneficiarios: event.items.map(item => item._id)
    }
    this.store$.dispatch(DeleteIntegranteGrupo({ id: form._id, integrante: payload }))
  }

  editarGrupo(grupo: IGrupo): void {
    this.grupoDuplicate = grupo;
    this.mode = 'EDITAR'
    this.header = 'Editar Grupo';
    this.formGrupo.patchValue({
      ...grupo
    });
    this.formGrupo.enable();
    this.formGrupo.controls.tamaño_integrantes.disable();

    this.displayForm = true;
  }

  verGrupo(grupo: IGrupo): void {
    this.mode = 'DETALLE'
    this.header = 'Detalle Grupo'
    this.formGrupo.patchValue({
      ...grupo
    });
    this.formGrupo.disable();

    this.displayForm = true;
  }

  agregarGrupo() {
    this.mode = 'AGREGAR'
    this.header = 'Agregar Grupo'
    this.formGrupo.patchValue({
      _id: '',
      nombre: '',
      fecha_inicio: '',
      fecha_fin: '',
      centro: '',
      tamaño_integrantes: '0',
      trabajador_asignado: '',
      programa: '',
      num_sesiones: '',
      horas_sesion: '',
      modalidad: '',
      dias: '',
      horario: '',
      tipo_servicio: ''
    })
    this.formGrupo.enable();
    this.formGrupo.controls.tamaño_integrantes.disable();

    this.displayForm = true;
  }

  borrarGrupo(grupo: IGrupo) {
    this.confirmationService.confirm({
      header: 'Borrar Grupo',
      message: `¿Seguro que quiere borrar el grupo '${grupo.nombre}'?`,
      acceptLabel: 'Aceptar',
      rejectVisible: false,
      accept: async () => {
        this.store$.dispatch(DeleteGrupo({ id: grupo._id }))
      }
    });
  }

  clone() {
    this.store$.dispatch(CreateGrupo({ grupo: this.grupoDuplicate }))
  }

  submitForm() {
    const form: IGrupo = this.formGrupo.getRawValue();
    if (this.mode === 'AGREGAR') {
      const payload: ICreateGrupo = {
        ...form,
      }

      this.store$.dispatch(CreateGrupo({ grupo: payload }));
    } else if (this.mode === 'EDITAR') {
      const payload: IUpdateGrupo = {
        ...form,
      }

      this.store$.dispatch(UpdateGrupo({ id: form._id, grupo: payload }));
    }

    return;
  }

  verFormularios(grupo: IGrupo) {
    this.header = 'Formularios';
    this.displayFormulario = true;
    this.formularioAsignadoData = { form: '', integrantes: grupo.integrantes || [] };
  }

  asignarFormulario() {
    this.spinner = true;
    const form = { ...this.formularioAsignadoData }
    form.form = this.formularioAsignado;
    this.store$.dispatch(CreateFormularioGrupo({ formulariosAsignados: form }));
  }

  async verAsistencia(grupo: IGrupo) {
    this.selectedGrupo = grupo;
    this.store$.dispatch(GetSesiones({ id: grupo._id }));
  }

  crearAsistencia(beneficiaria, sesion) {
    const data = {
      sesionId: sesion,
      integranteId: beneficiaria
    }

    this.store$.dispatch(CreateAsistencias({ asistencias: data }));
  }

  exportToExcel() {
    this.dataForExcel = [];

    const uniqueArray = this.gruposExcel.filter((value, index) => {
      const _value = JSON.stringify(value);
      return index === this.gruposExcel.findIndex(obj => {
        return JSON.stringify(obj) === _value;
      });
    });

    uniqueArray.forEach((row: any) => {
      const insert = { ...row, dias: row.dias.join(), integrantes: row.integrantes.join() }
      this.dataForExcel.push(Object.values(insert))
    })

    const headers = {
      id: '',
      nombre: '',
      'fecha de inicio': '',
      'fecha de fin': '',
      'colaborador asignado': '',
      centro: '',
      integrantes: '',
      programa: '',
      'numero sesiones': '',
      'horas por sesion': '',
      modalidad: '',
      dias: '',
      horario: '',
      'tipo de servicio': ''
    }

    const reportData = {
      title: 'Grupos',
      data: this.dataForExcel,
      headers: Object.keys(this.gruposExcel[0])
    }

    this.ete.exportExcel(reportData, false);
  }
}